import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, Phone, Camera, Music2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_joywin-photo-deals/artifacts/7835b5me_new%20logoo.PNG";
const JMAJOR_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/jdkdmeym_Jmajor-Photoroom.png";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  const typeParam = searchParams.get('type');
  const { register } = useAuth();

  const [accountType, setAccountType] = useState(typeParam === 'dj' ? 'dj' : '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDJ = accountType === 'dj';
  const accent = isDJ ? '#EF0000' : '#D4AF37';
  const currentLogo = isDJ ? JMAJOR_LOGO : LOGO_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(name, email, password, phone || null);
      toast.success('Account created successfully!');
      navigate(redirect);
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" data-testid="register-page"
      style={{ background: isDJ ? 'linear-gradient(135deg, #0a0000 0%, #1a0508 50%, #0a0000 100%)' : '#050505' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to={isDJ ? "/dj" : "/"}>
            <img 
              src={currentLogo} 
              alt={isDJ ? "J MAJOR" : "Joywin's Clicks"} 
              className="h-20 mx-auto object-contain"
            />
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-[#0a0a0a] rounded-2xl p-8 border border-white/10">
          <h1 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] text-center mb-6">
            Create Account
          </h1>

          {/* Account Type Selector */}
          {!typeParam && (
            <div className="mb-6">
              <label className="text-sm text-[#A1A1AA] block mb-3">I want to register for:</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setAccountType('photo')}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    accountType === 'photo' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/30'
                  }`}
                  data-testid="account-type-photo"
                >
                  <Camera className="w-6 h-6 mx-auto mb-2" style={{ color: accountType === 'photo' ? '#D4AF37' : '#52525B' }} />
                  <span className={`text-sm ${accountType === 'photo' ? 'text-[#EDEDED]' : 'text-[#A1A1AA]'}`}>Photography</span>
                  <p className="text-xs text-[#52525B] mt-1">Joywin's Clicks</p>
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType('dj')}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    accountType === 'dj' ? 'border-[#EF0000] bg-[#EF0000]/10' : 'border-white/10 hover:border-white/30'
                  }`}
                  data-testid="account-type-dj"
                >
                  <Music2 className="w-6 h-6 mx-auto mb-2" style={{ color: accountType === 'dj' ? '#EF0000' : '#52525B' }} />
                  <span className={`text-sm ${accountType === 'dj' ? 'text-[#EDEDED]' : 'text-[#A1A1AA]'}`}>DJ Booking</span>
                  <p className="text-xs text-[#52525B] mt-1">J MAJOR</p>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  className="pl-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="pl-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Phone (Optional)</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  className="pl-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  minLength={6}
                  className="pl-10 pr-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="pl-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !accountType}
              className={`w-full py-6 ${isDJ ? 'text-white' : 'gold-gradient-btn'}`}
              style={isDJ ? { background: '#EF0000' } : {}}
              data-testid="register-submit"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-[#A1A1AA] text-sm mt-6">
            Already have an account?{' '}
            <Link
              to={`/login${redirect !== '/' ? `?redirect=${redirect}` : ''}${isDJ ? '&type=dj' : ''}`}
              className="hover:underline"
              style={{ color: accent }}
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
